import { type ClassValue, clsx } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

// Extend tailwind-merge to understand custom design system
// https://stackoverflow.com/a/79231957/12988774
//
// By default, tailwind-merge groups classes that affect the same CSS property to prevent conflicts.
// For example, 'text-red-500' and 'text-blue-500' are in the same group because they both set color.
// When merging classes, tailwind-merge keeps only the last class from each group.
//
// However, our custom classes like 'text-size-lg' (font-size) and 'text-text-primary' (color) use the
// same 'text-' prefix but control different CSS properties. Without this configuration, tailwind-merge
// would incorrectly group them together and remove one when merging, even though they don't conflict.
//
// By defining separate classGroups, we tell tailwind-merge that:
// - 'text-size-lg', 'text-size-md', etc. only affect font-size
// - 'text-text-primary', 'text-text-secondary', etc. only affect color
// - These groups don't conflict with each other, so both classes can coexist
//
// This ensures that cn('text-size-lg text-text-primary') keeps both classes instead of dropping one.

// IMPORTANT: Keep these class lists in sync with:
// - src/styles/typography.css (text size CSS tokens)
// - src/styles/colors/colors.css (text color CSS tokens)
// - src/components/text/styles.ts (CVA variants)
//
// ⚠️  IF OUT OF SYNC, CLASS MERGING BREAKS SILENTLY!
// For example, if 'text-size-4xl' exists in typography.css but not here,
// then cn('text-size-4xl text-size-lg') keeps BOTH classes instead of
// merging them, causing unexpected style conflicts.
//
// Sync is validated by cn.test.ts - run `pnpm test` to check.
const customTwMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      // Custom font sizes (typography variants)
      'font-size': [
        'text-size-xs',
        'text-size-sm',
        'text-size-md',
        'text-size-lg',
        'text-size-xl',
        'text-size-2xl',
        'text-size-3xl',
      ],
      // Custom text colors (separate from font-size)
      // Keep in sync with --color-text-* and --color-icon-* tokens in colors/colors.css
      'text-color': [
        'text-text-accent',
        'text-text-primary',
        'text-text-secondary',
        'text-text-tertiary',
        'text-text-inverse',
        'text-text-neutral',
        'text-text-processing',
        'text-text-success-weak',
        'text-text-success',
        'text-text-warning',
        'text-text-danger-weak',
        'text-text-danger',
        'text-text-special',
        'text-text-nav-options-default',
        'text-text-nav-options-active',
        'text-text-nav-options-section-title',

        'text-icon-accent',
        'text-icon-primary',
        'text-icon-secondary',
        'text-icon-tertiary',
        'text-icon-inverse',
        'text-icon-neutral-weak',
        'text-icon-neutral',
        'text-icon-processing-weak',
        'text-icon-processing',
        'text-icon-success-weak',
        'text-icon-success',
        'text-icon-warning-weak',
        'text-icon-warning',
        'text-icon-danger-weak',
        'text-icon-danger',
        'text-icon-special-weak',
        'text-icon-special',
        'text-icon-nav-options-default',
        'text-icon-nav-options-active',
        'text-icon-controls-default',
        'text-icon-logo-default',
      ],
    },
    // Ensure these groups don't conflict with each other
    conflictingClassGroups: {
      'font-size': [], // font-size doesn't conflict with text-color
      'text-color': [], // text-color doesn't conflict with font-size
    },
  },
})

export function cn(...inputs: ClassValue[]): string {
  return customTwMerge(clsx(inputs))
}
